import { observer } from 'mobx-react-lite';
import { AnimatePresence } from 'motion/react';
import { useLayoutEffect, useRef, useState } from 'react';

import { Icons } from '@/assets/icons/icons';
import { SHIMMER_TEXT_CLASSES } from '@/components/blocks/Message/reasoning/ShimmerCollapsibleHeader';
import { XStack, YStack } from '@/components/ui';
import { Empty } from '@/components/ui/EmptyState';
import { TranscriptionInputDeviceDropdown } from '@/features/transcription-input-device';
import { cn } from '@/lib/utils';
import { AppCapability, isCapable } from '@/utils/capabilities';
import { classList } from '@/utils/cn';

import { FadeView } from '../FadeView';
import { TranscriptConsentNotice } from '../meetings-transcript-display.components';
import { useProcessedTranscripts } from '../meetings-transcript-display.hooks';
import {
  ACTIVE_MEETING_INPUT_DEVICE_CONFIG,
  MEETING_PREFERENCE_INPUT_DEVICE_CONFIG,
} from '../meetings-transcription-input-device.config';
import { meetingApis } from '../meetings.query';
import { meetingStore } from '../meetings.state';

import { CopyTranscriptButton } from './CopyTranscriptButton';
import { TranscriptHeading } from './TranscriptHeading';
import { Transcripts } from './Transcripts';
import { TranscriptTooltipSessionProvider } from './TranscriptTooltip';
import { useMeetingWithoutTranscript } from './use-meeting-without-transcript';

import type { MeetingId } from '../meetings.types';

type MeetingTranscriptProps = { meetingId: MeetingId; isInPopup?: boolean };

export const MeetingTranscriptView = observer(
  ({ meetingId, isInPopup = false }: MeetingTranscriptProps) => {
    const viewRef = useRef<HTMLDivElement>(null);
    const pendingFocusRequest = useRef(false);
    const [focusRequest, setFocusRequest] = useState<{
      sourceLineIndex: number | undefined;
    }>();
    const transcript = meetingStore.getCommittedTranscript(meetingId);
    const transcription = meetingStore.getTranscriptDisplayLines(meetingId);

    const { meeting, isMeetingBeingRecorded } =
      useMeetingWithoutTranscript(meetingId);

    const transcripts = useProcessedTranscripts(
      transcription,
      meeting?.attendees,
      meetingStore.getResolvedSpeakerNames(meetingId)
    );
    useLayoutEffect(() => {
      if (!focusRequest || !pendingFocusRequest.current) return;
      if (!transcripts.length) return;
      pendingFocusRequest.current = false;
      const root = viewRef.current;
      const target =
        root?.querySelector<HTMLElement>(
          `[data-transcript-key="${focusRequest.sourceLineIndex}"][aria-disabled="false"]`
        ) ??
        root?.querySelector<HTMLElement>(
          '[role="option"][aria-disabled="false"]'
        );
      (target ?? root)?.focus();
    }, [focusRequest, transcripts.length]);
    const isPrepState = meetingStore.isPrepState(meetingId);
    const canRecord = isCapable(AppCapability.RecordMeeting);
    const isPostProcessing =
      meetingStore.getPostProcessingJob(meetingId)?.state === 'running';
    const shouldShowConsent =
      !isPostProcessing && (isPrepState || isMeetingBeingRecorded);
    const { data: transcriptDeletionEnabled = false } =
      meetingApis.useTranscriptDeletionEnabledQuery();
    const canDeleteChunks =
      transcriptDeletionEnabled &&
      !meetingStore.isMeetingInRecordingLifecycle(meetingId) &&
      !meetingStore.getPostProcessingJob(meetingId) &&
      transcription.every((line) =>
        line.segments.every((segment) => segment.isFinal)
      );

    const transcriptEmptyState =
      transcripts.length > 0
        ? null
        : isMeetingBeingRecorded
          ? 'listening'
          : isPrepState && canRecord
            ? 'ready'
            : 'nothing-captured';

    return (
      <YStack
        ref={viewRef}
        role="region"
        aria-label="Transcript"
        tabIndex={-1}
        className="h-full outline-none focus-visible:ring-2 focus-visible:ring-accent"
        data-testid="meeting_transcript_content"
      >
        {meeting && (
          <TranscriptHeading isInPopup={isInPopup} meeting={meeting} />
        )}
        {!isInPopup && (
          <XStack className="px-6 pt-5 pb-3 items-center justify-between gap-2">
            {isPostProcessing ? (
              <span
                className={cn(
                  'text-sm bg-clip-text text-transparent',
                  ...SHIMMER_TEXT_CLASSES
                )}
                data-testid="meeting_post_processing_indicator"
              >
                Post-processing transcript and detecting speakers…
              </span>
            ) : shouldShowConsent ? (
              <TranscriptConsentNotice />
            ) : null}
            <XStack className="ml-auto items-center gap-2">
              <TranscriptionInputDeviceDropdown
                size="default"
                variant="compact"
                config={
                  isMeetingBeingRecorded
                    ? ACTIVE_MEETING_INPUT_DEVICE_CONFIG
                    : MEETING_PREFERENCE_INPUT_DEVICE_CONFIG
                }
              />
              <CopyTranscriptButton meetingId={meetingId} />
            </XStack>
          </XStack>
        )}
        <AnimatePresence initial={false} mode="wait">
          {!transcripts.length && (
            <FadeView
              key="no-transcript"
              className={cn(
                'flex items-center justify-center size-full',
                !isInPopup && '-translate-y-11 pointer-events-none touch-none'
              )}
            >
              <Empty.Root
                ref={(node) => {
                  if (node && pendingFocusRequest.current) {
                    pendingFocusRequest.current = false;
                    node.focus();
                  }
                }}
                role="region"
                aria-label="Empty transcript"
                tabIndex={-1}
                className="rounded-lg outline-none focus-visible:ring-2 focus-visible:ring-accent"
                variant="plain"
                offset={!isInPopup ? 'default' : undefined}
                data-testid="meeting_transcript_empty_state"
                data-transcript-state={transcriptEmptyState ?? undefined}
              >
                <Empty.Media variant="icon">
                  {isMeetingBeingRecorded ? (
                    <Icons.audio />
                  ) : isPrepState && canRecord ? (
                    <Icons.playAlt />
                  ) : (
                    <Icons.transcripts />
                  )}
                </Empty.Media>
                <Empty.Header>
                  <Empty.Title>
                    {isMeetingBeingRecorded
                      ? 'Listening...'
                      : meeting?.transcript_deleted_at
                        ? 'Transcript removed'
                        : isPrepState && canRecord
                          ? 'Ready to start?'
                          : 'Nothing captured'}
                  </Empty.Title>
                  <Empty.Description>
                    {isMeetingBeingRecorded
                      ? 'Start speaking to see your transcript here'
                      : meeting?.transcript_deleted_at
                        ? "Transcript removed by your team's data policy"
                        : isPrepState
                          ? canRecord
                            ? 'Tap Start now to capture your transcript'
                            : 'Use the desktop app to record meeting audio'
                          : 'No speech was detected during this meeting'}
                  </Empty.Description>
                </Empty.Header>
              </Empty.Root>
            </FadeView>
          )}
          {transcripts.length > 0 && (
            <FadeView
              key="transcripts"
              className={classList('size-full', !isInPopup && 'pb-10')}
            >
              <TranscriptTooltipSessionProvider key={meetingId}>
                <Transcripts
                  onSelectionCleared={(sourceLineIndex) => {
                    pendingFocusRequest.current = true;
                    setFocusRequest({ sourceLineIndex });
                  }}
                  id={meetingId}
                  transcript={transcript}
                  isInPopup={isInPopup}
                  transcripts={transcripts}
                  canDeleteChunks={canDeleteChunks}
                  autoScroll={isMeetingBeingRecorded}
                  isRecording={isMeetingBeingRecorded}
                  attendees={meeting?.attendees}
                  canEditSpeakers={
                    !isInPopup && !meetingStore.getPostProcessingJob(meetingId)
                  }
                />
              </TranscriptTooltipSessionProvider>
            </FadeView>
          )}
        </AnimatePresence>
      </YStack>
    );
  }
);
